import type { Rules } from "./types.ts";

/**
 * The attribute rules (ADR-138 decisions 2, 7 and 8): the numbers the rules' constants hold, the effective value an
 * attribute has under its effects, and the combat factors a set of effective values gives. Pure whole-number maths: the
 * map server computes the factors when the values or the effects change and caches them; combat reads a number.
 *
 * A rate is held in basis points of the thing it changes for each point from the start (`d = effective - start`).
 * A factor is a multiplier in basis points, 10,000 being exactly 1, or a change in basis points, 0 being none; a
 * character at the start has every factor at that neutral value.
 */

const MAX_EFFECTIVE_OVER_CAP = 20;
const IDENTITY = 10_000;

/**
 * The most an effective attribute stands above `attribute_cap`: effects may lift a value this far past what points can.
 */
export const ATTRIBUTE_EFFECTIVE_MARGIN = MAX_EFFECTIVE_OVER_CAP;

/**
 * The numbers the attributes read, each a rules constant: the start, the cap and the points a level, and the combat
 * rates and limits of ADR-138 decision 8, all in basis points.
 */
export interface AttributeKnobs {
  readonly start: number;
  readonly cap: number;
  readonly pointsPerLevel: number;
  readonly vitalityHealth: number;
  readonly vitalityTaken: number;
  readonly vitalityTakenMax: number;
  readonly mightDamage: number;
  readonly agilitySpeed: number;
  readonly agilitySpeedMax: number;
  readonly agilityCooldown: number;
  readonly agilityCooldownMax: number;
  readonly agilityDodge: number;
  readonly agilityDodgeMax: number;
  readonly willpowerFocus: number;
  readonly willpowerRegeneration: number;
  readonly willpowerControl: number;
  readonly willpowerControlMax: number;
  readonly perceptionCrit: number;
  readonly perceptionCritFloor: number;
  readonly perceptionCritMax: number;
  readonly intellectDamage: number;
}

/**
 * The attribute knobs a set of rules holds.
 *
 * @param rules The rules, or anything holding their constants.
 * @returns The knobs.
 */
export function attributeKnobsOf(rules: Pick<Rules, "constants">): AttributeKnobs {
  const c = rules.constants;
  return {
    start: c.attribute_start,
    cap: c.attribute_cap,
    pointsPerLevel: c.attribute_points_per_level,
    vitalityHealth: c.attribute_vitality_health,
    vitalityTaken: c.attribute_vitality_taken,
    vitalityTakenMax: c.attribute_vitality_taken_max,
    mightDamage: c.attribute_might_damage,
    agilitySpeed: c.attribute_agility_speed,
    agilitySpeedMax: c.attribute_agility_speed_max,
    agilityCooldown: c.attribute_agility_cooldown,
    agilityCooldownMax: c.attribute_agility_cooldown_max,
    agilityDodge: c.attribute_agility_dodge,
    agilityDodgeMax: c.attribute_agility_dodge_max,
    willpowerFocus: c.attribute_willpower_focus,
    willpowerRegeneration: c.attribute_willpower_regeneration,
    willpowerControl: c.attribute_willpower_control,
    willpowerControlMax: c.attribute_willpower_control_max,
    perceptionCrit: c.attribute_perception_crit,
    perceptionCritFloor: c.attribute_perception_crit_floor,
    perceptionCritMax: c.attribute_perception_crit_max,
    intellectDamage: c.attribute_intellect_damage,
  };
}

/**
 * An attribute's effective value (ADR-138 decision 7): the stored value with the flat points of the effects on its
 * bearer added, held to 0 and to `attribute_cap + ATTRIBUTE_EFFECTIVE_MARGIN`.
 *
 * @param stored The stored value.
 * @param flat The sum over the bearer's effects of stacks times the effect's points for the attribute.
 * @param knobs The attribute knobs.
 * @returns The effective value, a whole number.
 */
export function effectiveAttribute(stored: number, flat: number, knobs: AttributeKnobs): number {
  return Math.min(knobs.cap + MAX_EFFECTIVE_OVER_CAP, Math.max(0, stored + flat));
}

/**
 * The combat factors, in the order a cache holds them: maximum health (multiplier), physical damage taken (change),
 * physical damage dealt (multiplier), movement speed (change), basic blow cooldown (change), dodge chance (chance, 0 or
 * more), maximum Focus (multiplier), Focus regeneration (multiplier), crowd control duration on the bearer (change),
 * critical chance (change) and non-physical ability damage (multiplier). All are basis points.
 */
export const ATTRIBUTE_FACTOR_NAMES = ["maxHealth", "physicalTaken", "physicalDealt", "moveSpeed", "blowCooldown", "dodge", "maxFocus", "focusRegeneration", "controlDuration", "critical", "nonPhysicalDealt"] as const;
/**
 * The name of one combat factor.
 */
export type AttributeFactorName = (typeof ATTRIBUTE_FACTOR_NAMES)[number];

/**
 * A set of combat factors by name.
 */
export type AttributeFactors = Readonly<Record<AttributeFactorName, number>>;

/**
 * The factors of a character whose seven attributes all stand at the start, in `ATTRIBUTE_FACTOR_NAMES` order: every
 * multiplier exactly 10,000 and every change 0.
 */
export const ATTRIBUTE_NEUTRAL_FACTORS: readonly number[] = Object.freeze([IDENTITY, 0, IDENTITY, 0, 0, 0, IDENTITY, IDENTITY, 0, 0, IDENTITY]);

/**
 * Holds a number to a range, never returning negative zero.
 *
 * @param value The number.
 * @param low The least.
 * @param high The most.
 * @returns The number within the range.
 */
function held(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value)) + 0;
}

/**
 * Writes the combat factors of seven effective values into an array, allocating nothing.
 *
 * @param effective The seven effective values in `ATTRIBUTES` order (might, agility, vitality, intellect, willpower, perception, presence).
 * @param knobs The attribute knobs.
 * @param out Where the factors go, in `ATTRIBUTE_FACTOR_NAMES` order.
 * @param offset The index of the first factor in `out`.
 */
export function attributeFactorsInto(effective: ArrayLike<number>, knobs: AttributeKnobs, out: { [index: number]: number }, offset = 0): void {
  const might = (effective[0] as number) - knobs.start;
  const agility = (effective[1] as number) - knobs.start;
  const vitality = (effective[2] as number) - knobs.start;
  const intellect = (effective[3] as number) - knobs.start;
  const willpower = (effective[4] as number) - knobs.start;
  const perception = (effective[5] as number) - knobs.start;
  out[offset] = Math.max(0, IDENTITY + knobs.vitalityHealth * vitality);
  out[offset + 1] = held(-knobs.vitalityTaken * vitality, -knobs.vitalityTakenMax, knobs.vitalityTakenMax);
  out[offset + 2] = Math.max(0, IDENTITY + knobs.mightDamage * might);
  out[offset + 3] = held(knobs.agilitySpeed * agility, -knobs.agilitySpeedMax, knobs.agilitySpeedMax);
  out[offset + 4] = held(-knobs.agilityCooldown * agility, -knobs.agilityCooldownMax, knobs.agilityCooldownMax);
  out[offset + 5] = held(knobs.agilityDodge * Math.max(0, agility), 0, knobs.agilityDodgeMax);
  out[offset + 6] = Math.max(0, IDENTITY + knobs.willpowerFocus * willpower);
  out[offset + 7] = Math.max(0, IDENTITY + knobs.willpowerRegeneration * willpower);
  out[offset + 8] = held(-knobs.willpowerControl * willpower, -knobs.willpowerControlMax, knobs.willpowerControlMax);
  out[offset + 9] = held(knobs.perceptionCrit * perception, -knobs.perceptionCritFloor, knobs.perceptionCritMax);
  out[offset + 10] = Math.max(0, IDENTITY + knobs.intellectDamage * intellect);
}

/**
 * The combat factors of seven effective values (ADR-138 decision 8), every formula relative to the start so that a
 * character at the start is unchanged.
 *
 * @param effective The seven effective values in `ATTRIBUTES` order.
 * @param knobs The attribute knobs.
 * @returns The factors by name, in basis points.
 * @example
 * attributeFactors([15, 5, 5, 5, 5, 5, 5], knobs).physicalDealt;
 * // 12000
 */
export function attributeFactors(effective: ArrayLike<number>, knobs: AttributeKnobs): AttributeFactors {
  const out = new Array<number>(ATTRIBUTE_FACTOR_NAMES.length).fill(0);
  attributeFactorsInto(effective, knobs, out);
  return Object.fromEntries(ATTRIBUTE_FACTOR_NAMES.map((name, index) => [name, out[index]])) as AttributeFactors;
}
